import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../axiosInstance';
import { toast } from 'react-toastify';
import makeStyles from '@mui/styles/makeStyles';
import Grid from 'apollo-react/components/Grid';
import Paper from 'apollo-react/components/Paper';
import Typography from 'apollo-react/components/Typography';
import Button from 'apollo-react/components/Button';
import Divider from 'apollo-react/components/Divider';
import CircularProgress from 'apollo-react/components/CircularProgress';
import WorkflowInit from './builder_init';

const styles = {
  paper: { padding: '22px', marginBottom: '16px' },
  header: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontWeight: 600,
  },
  infoGrid: {
    marginTop: '16px',
  },
  infoLabel: {
    fontWeight: 600,
    color: '#666',
    marginBottom: '4px',
  },
  infoValue: {
    color: '#333',
  },
  imageContainer: {
    marginTop: '16px',
    textAlign: 'center',
    maxWidth: '100%',
  },
  workflowImage: {
    maxWidth: '100%',
    maxHeight: '400px',
    objectFit: 'contain',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  noImage: {
    padding: '60px 20px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    textAlign: 'center',
    color: '#999',
  },
  workflowContainer: {
    minHeight: '500px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    marginTop: '16px',
  },
  button: {
    minWidth: '100px',
    padding: '8px 16px',
  },
  viewOnlyBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    marginBottom: '16px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    marginLeft: '12px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
};

const useStyles = makeStyles(styles);

const WorkflowViewer = () => {
  const { vId: versionId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const { t, ready, i18n } = useTranslation();
  const workflowBuilderRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [workflowVersion, setWorkflowVersion] = useState(null);
  const [preJsonData, setPreJsonData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {}, [i18n.language]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        window.URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    const fetchWorkflowVersion = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows/version/${versionId}`,
          { withCredentials: true }
        );

        if (response.data && response.data.data) {
          const versionData = response.data.data;
          setWorkflowVersion(versionData);
          setPreJsonData(versionData.stages_json);

          if (versionData.workflow_image_id) {
            try {
              const imageResponse = await axiosInstance.get(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/files/admin/preview/${versionData.workflow_image_id}`,
                { 
                  withCredentials: true,
                  responseType: 'blob'
                }
              );
              
              const blob = new Blob([imageResponse.data], { 
                type: imageResponse.headers['content-type'] || 'image/jpeg'
              });
              const url = window.URL.createObjectURL(blob);
              setImageUrl(url);
            } catch (imageError) {
              console.warn('Could not load workflow image:', imageError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching workflow version:', error);
        toast.error(t('Error loading workflow version'), {
          position: 'top-center',
        });
      } finally {
        setLoading(false);
      }
    };

    if (versionId) {
      fetchWorkflowVersion();
    }
  }, [versionId, t]);

  if (!ready || loading) {
    return (
      <Grid container spacing={2}>
        <Grid item md={12}>
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: '16px' }}>
              {t('Loading...')}
            </Typography>
          </div>
        </Grid>
      </Grid>
    );
  }

  if (!workflowVersion) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Typography variant="body2" style={{ color: '#f44336' }}>
              {t('Workflow not found.')}
            </Typography>
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className={classes.button}
              style={{ marginTop: '16px' }}
            >
              {t('Back')}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className={classes.paper}>

          <div style={{ marginTop: '24px' }}>
            <Typography variant="h6" className={classes.sectionTitle}>
              {t('Workflow Information')}
            </Typography>

            <Divider />

            <Grid container spacing={3} className={classes.infoGrid}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" className={classes.infoLabel}>
                  {t('Name')}
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {workflowVersion.name}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" className={classes.infoLabel}>
                  {t('Version')}
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  V{workflowVersion.version}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" className={classes.infoLabel}>
                  {t('Creation Date')}
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {new Date(workflowVersion.createdAt).toLocaleString('en-GB')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" className={classes.infoLabel}>
                  {t('Published At')}
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {workflowVersion.publishedAt
                    ? new Date(workflowVersion.publishedAt).toLocaleString('en-GB')
                    : t('Not Published')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" className={classes.infoLabel}>
                  {t('Description')}
                </Typography>
                <Typography variant="body1" className={classes.infoValue}>
                  {workflowVersion.description || t('No Description')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" className={classes.infoLabel}>
                  {t('Workflow Image')}
                </Typography>
                <div className={classes.imageContainer}>
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={workflowVersion.name}
                      className={classes.workflowImage}
                    />
                  ) : (
                    <div className={classes.noImage}>
                      <Typography variant="body2">{t('No Image Available')}</Typography>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
<div style={{ marginTop: '24px' }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('Workflow Builder')}
          </Typography>

          <Divider />

          <div className={classes.workflowContainer}>
            {preJsonData ? (
              <WorkflowInit ref={workflowBuilderRef} preJsonData={preJsonData} />
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  color: '#666',
                }}
              >
                <Typography variant="body2">
                  {t('No workflow diagram available')}
                </Typography>
              </div>
            )}
          </div>
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default WorkflowViewer;

